import request from './requestServices';

const billingService = {
  /**
   * Get current user's subscription information
   * Returns subscription data object
   */
  async getCurrentSubscription() {
    try {
      const { data } = await request('/v1/billing/subscription', {
        method: 'GET',
      });
      // The request function already extracts the top-level 'data' field,
      // so we need to return the nested 'data' field that contains the subscription info
      return data?.data || null;
    } catch (err) {
      console.error('[Billing] Failed to get current subscription:', err);
      // Return null instead of throwing to prevent UI crashes
      return null;
    }
  },

  /**
   * Get available subscription plans (authenticated)
   * Returns plans array
   */
  async getAvailablePlans() {
    try {
      console.log('[Billing] Calling /v1/billing/plans...');
      const response = await request('/v1/billing/plans', {
        method: 'GET',
      });
      console.log('[Billing] Plans API response:', response);
      return response.data;
    } catch (err) {
      console.error('[Billing] Failed to get available plans:', err);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  },

  /**
   * Get available subscription plans (public, no authentication required)
   * Returns plans array
   */
  async getPublicPlans() {
    try {
      console.log('[Billing] Calling /v1/billing/public/plans...');
      const response = await request('/v1/billing/public/plans', {
        method: 'GET',
      });
      console.log('[Billing] Public plans API response:', response);
      return response.data;
    } catch (err) {
      console.error('[Billing] Failed to get public plans:', err);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  },


  /**
   * Create customer portal session for billing management
   * Returns portal session URL
   */

  /**
   * Get billing history for current user
   * Returns billing history array
   */
  async getBillingHistory() {
    try {
      const { data } = await request('/v1/billing/history', {
        method: 'GET',
      });
      return data;
    } catch (err) {
      console.error('[Billing] Failed to get billing history:', err);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  },

  /**
   * Cancel current subscription
   * Returns success status and message
   */
  async cancelSubscription() {
    try {
      const { data } = await request('/v1/billing/cancel', {
        method: 'POST',
        data: {}, // Send empty JSON object to ensure Content-Type is application/json
      });
      return data;
    } catch (err) {
      console.error('[Billing] Failed to cancel subscription:', err);
      throw err;
    }
  },

  /**
   * Create Stripe customer portal session for subscription management
   * Returns portal URL
   */
  async createCustomerPortalSession(returnUrl) {
    try {
      const { data } = await request('/v1/billing/customer-portal', {
        method: 'POST',
        data: {
          return_url: returnUrl,
        },
      });
      return data?.data?.portal_url;
    } catch (err) {
      console.error('[Billing] Failed to create customer portal session:', err);
      throw err;
    }
  },

  /**
   * Create Stripe checkout session for subscription
   * Returns checkout session URL
   */
  async createCheckoutSession(planId) {
    try {
      const { data } = await request('/v1/billing/create-checkout-session', {
        method: 'POST',
        data: {
          plan_id: planId,
        },
      });
      return data?.data?.checkout_url;
    } catch (err) {
      console.error('[Billing] Failed to create checkout session:', err);
      throw err;
    }
  },

  /**
   * Create Stripe Checkout session for trial signup with payment method collection
   * Returns checkout session URL
   */
  async createTrialCheckoutSession() {
    try {
      console.log('[Billing] Creating trial checkout session...');
      const { data } = await request('/v1/billing/create-trial-checkout-session', {
        method: 'POST',
        data: {}, // Send empty JSON object to ensure Content-Type is application/json
      });
      console.log('[Billing] Trial checkout session response:', data);
      
      // Check if subscription was synced from existing Stripe subscription
      if (data?.data?.subscription_synced) {
        return {
          subscription_synced: true,
          redirect: data?.data?.redirect || '/landing'
        };
      }
      
      // Otherwise return the checkout URL
      return data?.data?.checkout_url;
    } catch (err) {
      console.error('[Billing] Failed to create trial checkout session:', err);
      throw err;
    }
  },

  /**
   * Verify and sync subscription from Stripe Checkout session
   * Call this when user returns from checkout with session_id
   * Returns sync status
   */
  async verifyCheckoutSession(sessionId) {
    try {
      console.log('[Billing] Verifying checkout session:', sessionId);
      const { data } = await request('/v1/billing/verify-checkout-session', {
        method: 'POST',
        data: { session_id: sessionId },
      });
      console.log('[Billing] Verify checkout session response:', data);
      return data;
    } catch (err) {
      console.error('[Billing] Failed to verify checkout session:', err);
      throw err;
    }
  },

  /**
   * Purchase credits - tries direct charge first, falls back to checkout.
   * 
   * Returns:
   * - { direct_purchase: true, success: true, amount, message } if direct charge succeeded
   * - { direct_purchase: false, checkout_url } if redirect to checkout is needed
   */
  async createCreditCheckoutSession(amount, description, successUrl, cancelUrl) {
    try {
      console.log('[Billing] Purchasing credits:', {
        amount,
        description,
        successUrl,
        cancelUrl
      });
      
      const { data } = await request('/v1/billing/create-checkout-session', {
        method: 'POST',
        data: {
          amount: amount,
          currency: 'usd',
          description: description,
          success_url: successUrl,
          cancel_url: cancelUrl,
        },
      });
      
      console.log('[Billing] Credit purchase response:', data);
      
      // Return the full response object so caller can check direct_purchase flag
      return data?.data;
    } catch (err) {
      console.error('[Billing] Failed to purchase credits:', err);
      console.error('[Billing] Error details:', err.message, err.status, err.retCode);
      throw err;
    }
  },

  /**
   * Get current user's credit balance from Stripe
   * Returns credit balance information
   */
  async getCreditBalance() {
    try {
      const { data } = await request('/v1/billing/credit-balance', {
        method: 'GET',
      });
      return data?.data || {
        available_credits: 0.0,
        ledger_credits: 0.0,
        monthly_subscription_value: 0.0,
        total_available: 0.0,
        currency: 'usd'
      };
    } catch (err) {
      console.error('[Billing] Failed to get credit balance:', err);
      // Return default values instead of throwing to prevent UI crashes
      return {
        available_credits: 0.0,
        ledger_credits: 0.0,
        monthly_subscription_value: 0.0,
        total_available: 0.0,
        currency: 'usd'
      };
    }
  },

  /**
   * Grant subscription credits to current user (for existing users)
   * Returns success status and amount granted
   */
  async grantSubscriptionCredits() {
    try {
      const { data } = await request('/v1/billing/grant-subscription-credits', {
        method: 'POST',
        data: {}, // Send empty JSON object to ensure Content-Type is application/json
      });
      return data?.data || { message: 'Credits granted', amount: 0 };
    } catch (err) {
      console.error('[Billing] Failed to grant subscription credits:', err);
      throw err;
    }
  },

  /**
   * Get current user's access category and feature permissions
   * Returns user status with category, permissions, and action needed
   */
  async getUserStatus() {
    try {
      const { data } = await request('/v1/billing/user-status', {
        method: 'GET',
      });
      return data?.data || null;
    } catch (err) {
      console.error('[Billing] Failed to get user status:', err);
      return null;
    }
  },

};

export default billingService;
